import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import QueryKeys from '@/utils/QueryKeys';
import { plato_api_scores_data_path } from '@/modules/routes';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import { ScoreData } from '@/components/shared/Grading/Navigation/BulkGrading/types';
import { IAssignment } from '@/components/staffer/Grading/types';

const getKey = (assignment: IAssignment) => {
  const { assignment_details: { exercise_type: exerciseType } } = assignment;

  const formattedType = exerciseType.match(/[A-Z][a-z]+/g).join('_');

  return `${formattedType}_id`.toLowerCase();
};

// Bulk-gradable rows for the question shown in Grade By Question; empty when the question can't be bulk graded.
const useQuestionScoresData = () => {
  const lessonId = new URLSearchParams(window.location.search).get('lesson_id');
  const {
    assignment,
    classroom,
    isQuestionMode,
    questionId,
  } = useGradingStore();
  // Legacy-scored reading assignments get one score for the whole reading: nothing to bulk grade per question.
  const hasPerQuestionScores = !assignment?.is_reading_assignment || assignment?.scoring_mode === 'question';

  const { data: scoresData, isFetching } = useQuery({
    enabled: isQuestionMode && hasPerQuestionScores && lessonId != null && !!(questionId && classroom && assignment),
    placeholderData: [],
    queryFn: () => (
      axios
        .get<{ data: ScoreData[] }>(plato_api_scores_data_path({
        assignment_id: assignment.id,
        classroom_id: classroom.id,
        lesson_id: lessonId,
        question_id: questionId,
        [getKey(assignment)]: assignment.assignment_details.exercise_id,
      }))
        .then(res => res.data.data)
    ),
    queryKey: [QueryKeys.ScoresData, questionId, classroom?.id, assignment?.id],
  });

  const rows = scoresData || [];
  if (rows.length === 0 || rows[0].questionAutoGraded || rows[0].promptOnly) return { isFetching, rows: [] };

  return { isFetching, rows };
};

export default useQuestionScoresData;
